import React, { useState, useEffect, useRef } from "react";
import "./QueryChatPopup.css";
import { messageService } from "../../../services/MessageService";
import { queryService } from "../../../services/QueryService";
import { X } from "lucide-react";
import { toast } from "../../../utils/toast";
import QueryAttachment from "../RenderImage/RenderImage";
import { LoginContext } from "../../../context/LoginContext";
import { useContext } from "react";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

interface Chat {
  id: string;
  type: "Student" | "Admin";
  message: string;
  g_creation_time?: number;
  user_id?: string;
  photo?: string;
}

export interface help_desk_query {
  id: string
  g_creation_time: number
  g_soft_delete: string
  queryId: string
  subject: string
  status: string
  category: string
  user_id: string
  user_name: string
  user_email: string
  drive_id: string
  last_updated_at: string
  g_modify_time: string
  photo?: string
}

interface QueryChatPopupProps {
  // isOpen: boolean;
  // onClose: () => void;
  // onBack: () => void;
  // queryId: string;
  // querySubject?: string;
  // query: help_desk_query;
  // onStatusChange?: () => void;
}

const QueryChatPopup: React.FC<QueryChatPopupProps> = ({
  // isOpen,
  // onClose,
  // onBack,
  // queryId,
  // querySubject,
  // query,
  // onStatusChange
}) => {
  const { user } = useContext(LoginContext);

  const role = user.role || "";
  const isStudent = role.toLowerCase() === "student";
  const isAdmin = role.toLowerCase() === "admin";

  const navigate = useNavigate();
  const location = useLocation();
  const querySubject = location.state?.querySubject ?? "";
  // const query =  location.state?.query ?? null;
  const { id: queryId } = useParams();
  const isOpen = true
  const onClose = () => {
    if (isStudent) {
      navigate("/myQuery")
    }
    if (isAdmin) {
      navigate("/allQueries", { state: { isOpen: true } })
    }
  }
  const onBack = () => {
    if (isStudent) {
      navigate("/myQuery")
    }
    if (isAdmin) {
      navigate("/allQueries", { state: { isOpen: true } })
    }
  }

  const [response, setResponse] = useState("");
  const [messages, setMessages] = useState<Chat[]>([]);
  const [query, setQuery] = useState<help_desk_query | null>(null)
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [queryStatus, setQueryStatus] = useState("");

  const [dataToSave, setDataToSave] = useState<any>({});
  const [photoError, setPhotoError] = useState<string>("");

  useEffect(() => {
    if (isOpen && queryId) {
      fetchQueryDetails();
      fetchMessages();
    }
  }, [isOpen, queryId]);

  useEffect(() => {
    if (query?.status) {
      setQueryStatus(query?.status)
    }
  }, [query])

  const fetchQueryDetails = async () => {
    setLoading(true);
    try {
      const data = await queryService.getByFilter(
        "GET_QUERY_DETAILS",
        `query_id%3A${queryId}`
      );
      setQuery(data?.[0])
    } catch (error) {
      console.error("Error fetching query:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const data = await messageService.getByFilter(
        "GET_MSGS_BY_QUERY_ID",
        `query_id%3A${queryId}`,
      );
      const formattedMessages: Chat[] = data.map((msg: any) => ({
        id: msg.id,
        type: msg.sendertype,
        message: msg.message,
        g_creation_time: msg.g_creation_time,
        user_id: msg.user_id,
        photo: msg.photo
      }));

      // Sort by creation time
      formattedMessages.sort(
        (a, b) => (a.g_creation_time || 0) - (b.g_creation_time || 0),
      );

      setMessages(formattedMessages);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return "";
    try {
      const date = new Date(timestamp);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "";
    }
  };

  if (!user) {
    toast.error("Session expired. Please log in again.");
    return;
  }

  const handleSubmit = async () => {
    if (!response.trim()) return;
    if (!queryId) return;

    if (photoError) {
      toast.error("Please fix the photo size error before saving.");
      return;
    }

    setSending(true);
    try {
      const userId = user?.userId || "";
      if (!userId) {
        toast.error("Session expired. Please log in again.");
        return;
      }

      // Determine sender type from roles
      const isAdmin = role.toLowerCase() === "admin";
      const senderType = isAdmin ? "Admin" : "Student";

      const messageObj: Record<string, string> = {
        sendertype: senderType,
        message: response,
        query_Id: queryId,
        user_id: userId,
      };

      const messageParams = new FormData();

      let selectedFile = null;
      selectedFile = Object.keys(dataToSave).filter((key) => dataToSave[key] instanceof File);
      if (selectedFile !== undefined && selectedFile.length > 0) {
        messageParams.append("photo", dataToSave[selectedFile[0]]);
        dataToSave[selectedFile[0]] = "";

        messageParams.append("description", "message attachment");
        messageParams.append("appId", "admission-iiitb");
        messageParams.append("dmsRole", "admin");
        messageParams.append("user_id", userId);
        messageParams.append("tags", "t1,t2,attend");
      }


      const jsonString = JSON.stringify(messageObj);
      const base64Encoded = btoa(unescape(encodeURIComponent(jsonString)));
      messageParams.append("resource", base64Encoded);

      const result = await messageService.create({
        params: messageParams,
      });

      if (result.errCode !== -1) {
        setResponse("");
        // Refresh messages
        fetchMessages();
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (query: help_desk_query, newStatus: string) => {
    setQueryStatus(newStatus)
    try {
      const updateObj: Record<string, string> = {
        id: query.id,
        subject: query.subject,
        category: query.category,
        status: newStatus,
        user_id: query.user_id,
        drive_id: query.drive_id || "",
      };
      const jsonString = JSON.stringify(updateObj);
      const base64Encoded = btoa(jsonString);

      await queryService.update(query.id, base64Encoded);
      // if (onStatusChange) onStatusChange();
    } catch (error) {
      console.error("Error updating query status:", error);
      toast.error("Failed to update status.");
    }
  };

  interface HtmlAsTextProps {
    htmlString: string;
  }

  function HtmlAsText({ htmlString }: HtmlAsTextProps): JSX.Element {
    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (divRef.current) {
        divRef.current.textContent = htmlString;
      }
    }, [htmlString]);

    return <div ref={divRef} />;
  }

  return (
    <div className="querychat-overlay">
      <div className="querychat-container">
        {/* Header */}
        <div className="querychat-header">
          <span className="querychat-back" onClick={onBack}>
            ← Back to All Queries
          </span>
          {query?.subject && (
            <span style={{ fontWeight: 600, fontSize: "14px" }}>
              {query?.subject}
            </span>
          )}
          <div>
            <button className="help-popup-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="querychat-body">
          {loading ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              Loading messages...
            </div>
          ) : messages.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              No messages yet.
            </div>
          ) : (
            messages.map((chat, index) => (
              <div key={chat.id || index}>
                {chat.type === "Student" && (
                  <>
                    <div className="querychat-user">
                      <div className="querychat-avatar">👤</div>
                      <div>
                        <div className="querychat-name">Applicant - {query?.user_email} ({query?.queryId})</div>
                        <div className="querychat-email">
                          {formatTimestamp(chat.g_creation_time)}
                        </div>
                      </div>
                    </div>

                    <div className="querychat-message user-msg">
                      <div dangerouslySetInnerHTML={{ __html: chat.message }}></div>
                    </div>
                    <div>
                      {/* <img src={imageUrl ?? ''} alt="Blob content" style={{ maxWidth: '50%' }} /> */}
                      {/* {chat.photo && */}
                      <QueryAttachment
                        photo={chat.photo}
                      />
                      {/* } */}
                    </div>
                  </>
                )}

                {chat.type === "Admin" && (
                  <>
                    <div className="querychat-admin-title">
                      Admin Response
                      <span className="querychat-date">
                        {formatTimestamp(chat.g_creation_time)}
                      </span>
                    </div>

                    <div className="querychat-message admin-msg">
                      <div dangerouslySetInnerHTML={{ __html: chat.message }}></div>
                    </div>
                    <div>
                      <QueryAttachment
                        photo={chat.photo}
                      />
                    </div>
                  </>
                )}
              </div>
            ))
          )}

          {/* Response Box */}
          {/* <div className="querychat-response">
            <input
              type="text"
              placeholder="Your Response...."
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmit();
              }}
            />

            <button onClick={handleSubmit} disabled={sending}>
              {sending ? "Sending..." : "Submit Response"}
            </button>
          </div> */}
        </div>


        <div className="querychat-footer">
          <div className="querychat-response">
            <div style={{ flex: 1 }}>
              <ReactQuill
                theme="snow"
                id="response"
                value={response}
                placeholder="Ask your query"
                onChange={setResponse} />
              <div className="help-form-row">
                <div className="help-field">
                  {/* <label>Upload Attachment</label> */}
                  <input
                    className="form-input file-input"
                    type="file"
                    name="photo"
                    accept="image/jpeg,image/png,image/jpg, application/pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      if (file && file.size > 1024 * 1024) {
                        setPhotoError("Photo size must be less than 1024 KB. Please choose a smaller file.");
                        e.target.value = "";
                        setDataToSave({ ...dataToSave, photo: null });
                        return;
                      } else {
                        setPhotoError("")
                        setDataToSave({
                          ...dataToSave,
                          photo: e.target.files?.[0] || null,
                        })
                      }
                    }
                    }
                  />
                  {photoError && <span style={{ color: "red", fontSize: "0.8rem", marginTop: "4px", display: "block" }}>{photoError}</span>}
                  <small style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px", display: "block" }}>
                    Max size: 1024 KB | Formats: JPG, JPEG, PNG, PDF
                  </small>
                </div>
              </div>
            </div>
            {query && <div style={{ display: "flex", flexDirection: "column", gap: 25, paddingBottom: 5 }}>
              <select
                className={queryStatus === "Open" ? "message-open" : "message-resolved"}
                value={queryStatus}
                onChange={(e) => {
                  e.stopPropagation();
                  handleStatusChange(query, e.target.value);
                }}
              // onClick={(e) => e.stopPropagation()}
              >
                <option value="Open" hidden={!isStudent} disabled={queryStatus === "In Progress"}>Open</option>
                <option value="Closed" hidden={!isStudent}>Closed</option>
                <option value="In Progress" disabled={isStudent} hidden={isStudent}>In Progress</option>
                <option value="Resolved" disabled={isStudent} hidden={isStudent}>Resolved</option>
              </select>
              <button onClick={handleSubmit} disabled={sending || queryStatus === "Closed"}>
                {sending ? "Sending..." : "Submit Response"}
              </button>
            </div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueryChatPopup;
